import { useCallback, useEffect, useMemo, useState } from 'react';
import { getQuoteRequests, updateQuoteRequest, deleteQuoteRequest } from '../api/api';
import AdminLayout from './AdminLayout';
import './Admin.css';

const STATUS_OPTIONS = ['New', 'Contacted', 'Quoted', 'Won', 'Lost', 'Completed'];
const TEMPERATURE_OPTIONS = ['Hot', 'Warm', 'Cold'];
const PRIORITY_OPTIONS = ['High', 'Medium', 'Low'];

const getToday = () => new Date().toISOString().slice(0, 10);

const normalizeQuote = (quote) => ({
  ...quote,
  status: quote.status || 'New',
  leadTemperature: quote.leadTemperature || quote.temperature || 'Warm',
  assignedTo: quote.assignedTo || '',
  followUpDate: quote.followUpDate || '',
  followUpTime: quote.followUpTime || '',
  priority: quote.priority || 'Medium',
  leadSource: quote.leadSource || quote.source || 'Website',
  dealValue: quote.dealValue || '',
  nextAction: quote.nextAction || '',
  internalNotes: quote.internalNotes || quote.notes || '',
  lastContactedAt: quote.lastContactedAt || '',
});

const isOverdue = (quote) => {
  if (!quote.followUpDate || ['Won', 'Lost', 'Completed'].includes(quote.status)) return false;
  return quote.followUpDate < getToday();
};

const formatDate = (dateValue) => {
  if (!dateValue) return 'Not set';
  return new Date(dateValue).toLocaleDateString();
};

const getStatusBadgeClass = (status) => {
  switch (status) {
    case 'New': return 'status-new';
    case 'Contacted': return 'status-contacted';
    case 'Quoted': return 'status-quoted';
    case 'Won':
    case 'Completed': return 'status-completed';
    case 'Lost': return 'status-lost';
    default: return 'status-new';
  }
};

const getTemperatureClass = (temperature) => {
  switch (temperature) {
    case 'Hot': return 'lead-hot';
    case 'Cold': return 'lead-cold';
    default: return 'lead-warm';
  }
};

const openNativePicker = (event) => {
  if (typeof event.currentTarget.showPicker === 'function') {
    try {
      event.currentTarget.showPicker();
    } catch (error) {
      event.currentTarget.focus();
    }
  }
};

function QuoteDetailsModal({ quoteRequest, onClose, onUpdate }) {
  const [form, setForm] = useState(() => normalizeQuote(quoteRequest));
  const [loading, setLoading] = useState(false);

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await onUpdate(quoteRequest.id, {
        status: form.status,
        leadTemperature: form.leadTemperature,
        assignedTo: form.assignedTo.trim(),
        followUpDate: form.followUpDate,
        followUpTime: form.followUpTime,
        priority: form.priority,
        leadSource: form.leadSource.trim(),
        dealValue: form.dealValue,
        nextAction: form.nextAction.trim(),
        internalNotes: form.internalNotes.trim(),
        lastContactedAt: form.lastContactedAt,
      });
      onClose();
    } catch (error) {
      alert('Failed to update CRM record: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const markContactedNow = () => {
    setForm((current) => ({
      ...current,
      status: current.status === 'New' ? 'Contacted' : current.status,
      lastContactedAt: new Date().toISOString(),
    }));
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content quote-modal crm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header crm-modal-header">
          <div>
            <span className="crm-kicker">CRM Lead #{quoteRequest.id}</span>
            <h2>{quoteRequest.name}</h2>
            <div className="crm-modal-badges">
              <span className={`status-badge ${getStatusBadgeClass(form.status)}`}>{form.status}</span>
              <span className={`lead-temp-badge ${getTemperatureClass(form.leadTemperature)}`}>{form.leadTemperature}</span>
              <span className="crm-owner-pill">{form.assignedTo || 'Unassigned'}</span>
            </div>
          </div>
          <button className="btn-close" onClick={onClose}>&times;</button>
        </div>

        <div className="crm-modal-grid">
          <section className="crm-panel">
            <h3>Customer</h3>
            <div className="crm-detail-list">
              <div>
                <span>Name</span>
                <strong>{quoteRequest.name}</strong>
              </div>
              <div>
                <span>Email</span>
                <a href={`mailto:${quoteRequest.email}`}>{quoteRequest.email}</a>
              </div>
              <div>
                <span>Phone</span>
                <a href={`tel:${quoteRequest.phone}`}>{quoteRequest.phone}</a>
              </div>
              <div>
                <span>Postcode</span>
                <strong>{quoteRequest.postcode || 'Not supplied'}</strong>
              </div>
            </div>
          </section>

          <section className="crm-panel">
            <h3>Project</h3>
            <div className="crm-detail-list">
              <div>
                <span>Product</span>
                <strong>{quoteRequest.productName || quoteRequest.flooringType || 'General enquiry'}</strong>
              </div>
              <div>
                <span>Flooring Type</span>
                <strong>{quoteRequest.flooringType || 'Not supplied'}</strong>
              </div>
              <div>
                <span>Area</span>
                <strong>{quoteRequest.installationArea || 'Not supplied'}</strong>
              </div>
              <div>
                <span>Size</span>
                <strong>{quoteRequest.areaSize ? `${quoteRequest.areaSize} sqm` : 'Not supplied'}</strong>
              </div>
            </div>
          </section>
        </div>

        {quoteRequest.message && (
          <section className="crm-panel crm-full-panel">
            <h3>Customer Message</h3>
            <p className="crm-message">{quoteRequest.message}</p>
          </section>
        )}

        {quoteRequest.requiresMeasurement === 'Yes' && (
          <section className="crm-panel crm-full-panel measurement-section">
            <h3>Measurement Request</h3>
            <div className="crm-detail-list crm-three-cols">
              <div>
                <span>Address</span>
                <strong>{quoteRequest.address || 'Not supplied'}</strong>
              </div>
              <div>
                <span>Preferred Date</span>
                <strong>{formatDate(quoteRequest.preferredDate)}</strong>
              </div>
              <div>
                <span>Preferred Time</span>
                <strong>{quoteRequest.preferredTime || 'Not supplied'}</strong>
              </div>
            </div>
          </section>
        )}

        <section className="crm-panel crm-full-panel">
          <div className="crm-panel-heading">
            <h3>Lead Management</h3>
            <button type="button" className="btn-secondary-action" onClick={markContactedNow}>
              Mark Contacted Now
            </button>
          </div>

          <div className="crm-form-grid">
            <label className="form-group">
              <span>Status</span>
              <select value={form.status} onChange={(e) => updateField('status', e.target.value)}>
                {STATUS_OPTIONS.map((status) => <option key={status} value={status}>{status}</option>)}
              </select>
            </label>

            <label className="form-group">
              <span>Lead Temperature</span>
              <select value={form.leadTemperature} onChange={(e) => updateField('leadTemperature', e.target.value)}>
                {TEMPERATURE_OPTIONS.map((temperature) => <option key={temperature} value={temperature}>{temperature}</option>)}
              </select>
            </label>

            <label className="form-group">
              <span>Assigned Staff</span>
              <input
                value={form.assignedTo}
                onChange={(e) => updateField('assignedTo', e.target.value)}
                placeholder="Type staff name"
              />
            </label>

            <label className="form-group">
              <span>Priority</span>
              <select value={form.priority} onChange={(e) => updateField('priority', e.target.value)}>
                {PRIORITY_OPTIONS.map((priority) => <option key={priority} value={priority}>{priority}</option>)}
              </select>
            </label>

            <label className="form-group">
              <span>Follow-up Date</span>
              <input type="date" value={form.followUpDate} onClick={openNativePicker} onFocus={openNativePicker} onChange={(e) => updateField('followUpDate', e.target.value)} />
            </label>

            <label className="form-group">
              <span>Follow-up Time</span>
              <input type="time" value={form.followUpTime} onClick={openNativePicker} onFocus={openNativePicker} onChange={(e) => updateField('followUpTime', e.target.value)} />
            </label>

            <label className="form-group">
              <span>Lead Source</span>
              <input value={form.leadSource} onChange={(e) => updateField('leadSource', e.target.value)} />
            </label>

            <label className="form-group">
              <span>Estimated Value</span>
              <input value={form.dealValue} onChange={(e) => updateField('dealValue', e.target.value)} placeholder="e.g. £2,500" />
            </label>
          </div>

          <label className="form-group">
            <span>Next Action</span>
            <input
              value={form.nextAction}
              onChange={(e) => updateField('nextAction', e.target.value)}
              placeholder="e.g. Send SPC sample quote, call after 4pm"
            />
          </label>

          <div className="crm-audit-row">
            <span>Submitted: {new Date(quoteRequest.createdAt).toLocaleString()}</span>
            <span>Last contacted: {form.lastContactedAt ? new Date(form.lastContactedAt).toLocaleString() : 'Not recorded'}</span>
          </div>
        </section>

        <section className="crm-panel crm-full-panel crm-notes-panel">
          <label className="form-group">
            <span>Internal Notes</span>
            <textarea
              rows="4"
              value={form.internalNotes}
              onChange={(e) => updateField('internalNotes', e.target.value)}
              placeholder="Private CRM notes for staff only"
            />
          </label>
        </section>

        <div className="modal-actions">
          <button type="button" className="btn-cancel" onClick={onClose}>Close</button>
          <button type="button" className="btn-save" onClick={handleSave} disabled={loading}>
            {loading ? 'Saving...' : 'Save CRM Record'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminQuoteRequests({ onLogout }) {
  const [quoteRequests, setQuoteRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterTemperature, setFilterTemperature] = useState('All');
  const [search, setSearch] = useState('');
  const token = localStorage.getItem('adminToken');

  const fetchQuoteRequests = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getQuoteRequests(token);
      setQuoteRequests(data.map(normalizeQuote));
    } catch (error) {
      alert('Failed to fetch quote requests: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchQuoteRequests();
  }, [fetchQuoteRequests]);

  const stats = useMemo(() => {
    const normalized = quoteRequests.map(normalizeQuote);
    return {
      total: normalized.length,
      hot: normalized.filter((quote) => quote.leadTemperature === 'Hot').length,
      overdue: normalized.filter(isOverdue).length,
      unassigned: normalized.filter((quote) => !quote.assignedTo).length,
    };
  }, [quoteRequests]);

  const filteredRequests = useMemo(() => {
    const term = search.trim().toLowerCase();
    return quoteRequests.filter((quote) => {
      const statusMatch = filterStatus === 'All' || quote.status === filterStatus;
      const tempMatch = filterTemperature === 'All' || quote.leadTemperature === filterTemperature;
      const searchMatch = !term || [
        quote.name,
        quote.email,
        quote.phone,
        quote.productName,
        quote.flooringType,
        quote.installationArea,
        quote.assignedTo,
        quote.nextAction,
      ].filter(Boolean).some((value) => value.toLowerCase().includes(term));

      return statusMatch && tempMatch && searchMatch;
    });
  }, [filterStatus, filterTemperature, quoteRequests, search]);

  const handleUpdateQuote = async (id, updates) => {
    const payload = {
      ...updates,
      crmUpdatedAt: new Date().toISOString(),
    };
    await updateQuoteRequest(id, payload, token);
    await fetchQuoteRequests();
  };

  const handleQuickUpdate = async (quote, updates) => {
    try {
      await handleUpdateQuote(quote.id, updates);
    } catch (error) {
      alert('Failed to update lead: ' + error.message);
    }
  };

  const handleDeleteQuote = async (id) => {
    if (window.confirm('Are you sure you want to delete this quote request?')) {
      await deleteQuoteRequest(id, token);
      await fetchQuoteRequests();
    }
  };

  return (
    <AdminLayout onLogout={onLogout}>
      <div className="admin-header crm-header">
        <div>
          <h1>Quote CRM</h1>
          <p>Manage enquiries, staff ownership, follow-ups, lead quality, and project progress.</p>
        </div>
      </div>

      <div className="crm-stats-grid">
        <div className="crm-stat-card">
          <span>Total Leads</span>
          <strong>{stats.total}</strong>
        </div>
        <div className="crm-stat-card">
          <span>Hot Leads</span>
          <strong>{stats.hot}</strong>
        </div>
        <div className="crm-stat-card">
          <span>Follow-up Due</span>
          <strong>{stats.overdue}</strong>
        </div>
        <div className="crm-stat-card">
          <span>Unassigned</span>
          <strong>{stats.unassigned}</strong>
        </div>
      </div>

      <div className="admin-filter-bar crm-filter-bar">
        <div>
          <span className="crm-filter-label">Pipeline Status</span>
          <div className="filter-chips">
            {['All', ...STATUS_OPTIONS].map(status => (
              <button
                key={status}
                className={`filter-chip ${filterStatus === status ? 'active' : ''}`}
                onClick={() => setFilterStatus(status)}
              >
                {status}
                {status === 'All'
                  ? ` (${quoteRequests.length})`
                  : ` (${quoteRequests.filter(q => q.status === status).length})`
                }
              </button>
            ))}
          </div>
        </div>

        <div>
          <span className="crm-filter-label">Lead Temperature</span>
          <div className="filter-chips">
            {['All', ...TEMPERATURE_OPTIONS].map(temperature => (
              <button
                key={temperature}
                className={`filter-chip ${filterTemperature === temperature ? 'active' : ''}`}
                onClick={() => setFilterTemperature(temperature)}
              >
                {temperature}
              </button>
            ))}
          </div>
        </div>

        <label className="crm-search">
          <span>Search CRM</span>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Name, phone, product, staff..."
          />
        </label>
      </div>

      {loading ? (
        <div className="loading-state">Loading quote CRM...</div>
      ) : filteredRequests.length === 0 ? (
        <div className="empty-state">
          <p>No CRM leads match the selected filters.</p>
        </div>
      ) : (
        <>
          <div className="data-table crm-table">
            <table>
              <thead>
                <tr>
                  <th>Lead</th>
                  <th>Project</th>
                  <th>Owner</th>
                  <th>Pipeline</th>
                  <th>Follow-up</th>
                  <th>Next Action</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRequests.map((quote) => (
                  <tr key={quote.id} className={isOverdue(quote) ? 'crm-overdue-row' : ''}>
                    <td>
                      <div className="crm-lead-cell">
                        <strong>{quote.name}</strong>
                        <a href={`mailto:${quote.email}`}>{quote.email}</a>
                        <a href={`tel:${quote.phone}`}>{quote.phone}</a>
                      </div>
                    </td>
                    <td>
                      <div className="product-info">
                        <strong>{quote.productName || quote.flooringType || 'General enquiry'}</strong>
                        <span className="product-type">{quote.installationArea || 'Area not supplied'}</span>
                      </div>
                    </td>
                    <td>
                      <input
                        className="crm-inline-input"
                        value={quote.assignedTo}
                        placeholder="Assign staff"
                        onChange={(e) => setQuoteRequests((current) => current.map((item) => item.id === quote.id ? { ...item, assignedTo: e.target.value } : item))}
                        onBlur={(e) => handleQuickUpdate(quote, { assignedTo: e.target.value.trim() })}
                      />
                    </td>
                    <td>
                      <div className="crm-badge-stack">
                        <span className={`status-badge ${getStatusBadgeClass(quote.status)}`}>{quote.status}</span>
                        <span className={`lead-temp-badge ${getTemperatureClass(quote.leadTemperature)}`}>{quote.leadTemperature}</span>
                      </div>
                    </td>
                    <td>
                      <div className="crm-follow-cell">
                        <input
                          type="date"
                          value={quote.followUpDate}
                          onClick={openNativePicker}
                          onFocus={openNativePicker}
                          onChange={(e) => handleQuickUpdate(quote, { followUpDate: e.target.value })}
                        />
                        {isOverdue(quote) && <span className="overdue-text">Overdue</span>}
                      </div>
                    </td>
                    <td>
                      <span className="crm-next-action">{quote.nextAction || 'Not set'}</span>
                    </td>
                    <td>
                      <div className="table-actions">
                        <button className="btn-edit" onClick={() => setSelectedQuote(quote)}>Open</button>
                        <button className="btn-delete" onClick={() => handleDeleteQuote(quote.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="data-list crm-mobile-list">
            {filteredRequests.map((quote) => (
              <div key={quote.id} className={`data-list-item quote-card crm-lead-card ${isOverdue(quote) ? 'is-overdue' : ''}`}>
                <div className="quote-card-header">
                  <div>
                    <h3 className="data-list-title">{quote.name}</h3>
                    <span className="quote-date">Submitted {formatDate(quote.createdAt)}</span>
                  </div>
                  <span className={`lead-temp-badge ${getTemperatureClass(quote.leadTemperature)}`}>{quote.leadTemperature}</span>
                </div>

                <div className="crm-card-pills">
                  <span className={`status-badge ${getStatusBadgeClass(quote.status)}`}>{quote.status}</span>
                  <span>{quote.assignedTo || 'Unassigned'}</span>
                  {isOverdue(quote) && <span className="overdue-pill">Overdue</span>}
                </div>

                <div className="quote-card-body">
                  <div className="quote-info-row">
                    <span className="info-label">Contact</span>
                    <span className="info-value">
                      <a href={`tel:${quote.phone}`}>{quote.phone}</a>
                    </span>
                  </div>
                  <div className="quote-info-row">
                    <span className="info-label">Product</span>
                    <span className="info-value product-highlight">{quote.productName || quote.flooringType || 'General enquiry'}</span>
                  </div>
                  <div className="quote-info-row">
                    <span className="info-label">Follow-up</span>
                    <span className="info-value">{formatDate(quote.followUpDate)} {quote.followUpTime}</span>
                  </div>
                  <div className="quote-info-row">
                    <span className="info-label">Next Action</span>
                    <span className="info-value">{quote.nextAction || 'Not set'}</span>
                  </div>
                </div>

                <div className="data-list-actions">
                  <button className="btn-edit" onClick={() => setSelectedQuote(quote)}>Open CRM</button>
                  <button className="btn-delete" onClick={() => handleDeleteQuote(quote.id)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {selectedQuote && (
        <QuoteDetailsModal
          quoteRequest={selectedQuote}
          onClose={() => setSelectedQuote(null)}
          onUpdate={handleUpdateQuote}
        />
      )}
    </AdminLayout>
  );
}
